import {
    convertValueUnit,
    getOrMakeUnitDefinitionFromMdsSiMarkup,
    getUnitDefinitionFromCfJsonForVariable,
    getUnitMarkupFromCapabilityVariable,
} from '@metservicedev/cf-units';
import _ from 'lodash';
import { DateTime, Duration, Interval } from 'luxon';

import type { IGraphData } from '../types';

import type { CfJson } from '@metservicedev/platforms-services-interface/src/model/CfJson';
import type { Capability } from '@mono/types/src/mds';
import { convertNetcdfjsonToLineByLineDatumsWithoutMoment } from '../../../utils/convertNetcdfjsonToLineByLineDatum';
import reduceDatasetsByViewblockState from '../../../utils/reduceDatasetsByViewblockState';
import { transformDurationsToTimeRange } from '../../../utils/timeRangeMagic';
import { calculateExtremes } from '../MinimaAndMaxima';
import chartjsStyles from '../chartjsStyles';
import defaultState from '../defaultState';
import { DEFAULT_CURVE, DEFAULT_SYMBOL } from '../defaults';
import SMOOTHING_OPTIONS from '../smoothingOptions';
import type { LineGraphState } from '../types';

const DEFAULT_DP = 4;
const FALLBACK_CHARTJS_STYLE = { type: 'line', chartJsType: 'line', allowed: true, opts: { showLine: true, pointRadius: 3 } };

const getDashArrayFromLineStyle = (style: string) => {
    return style === 'dotted line' ? '5, 5' : undefined;
};

const flipValue180 = (sourceUnit: string, value: number | string) => {
    const degrees = convertValueUnit({ from: sourceUnit, to: 'deg', value });
    const degreeValue = Number.parseFloat(degrees.toString());
    const normalisedValue = +((((degreeValue + 180) % 360) + 360) % 360);
    return convertValueUnit({ from: 'deg', to: sourceUnit, value: normalisedValue });
};

const mergeTemplateAndData = (state: LineGraphState) => {
    const { model, datasets, viewblockState, time } = state;
    if (!datasets || !viewblockState) return state;

    const reportTime = time ? DateTime.fromISO(time.iso8601).setZone(time.timezone) : DateTime.local();
    const reducedDatasets = reduceDatasetsByViewblockState({ datasets, viewblockState }).map((dataset) => {
        if (dataset.error) {
            // TODO render something special when data is null
            return null;
        }

        const data = convertNetcdfjsonToLineByLineDatumsWithoutMoment({ netcdfjson: dataset.cfjson });

        return { ...dataset, data };
    });

    const startOfUnit = viewblockState.settings.truncateNowTo || defaultState.settings.truncateNowTo!; //defaultstate will always have fallback values

    const timeRangeItems =
        viewblockState.databilities.length > 0
            ? viewblockState.databilities.map((datability) => {
                  return datability.timeRange
                      ? {
                            start: Duration.fromISO(datability.timeRange.split('--')[0]).plus({ days: 1 }),
                            end: Duration.fromISO(datability.timeRange.split('--')[1]).minus({ days: 1 }),
                        }
                      : {
                            start: Duration.fromISO('P0D'),
                            end: Duration.fromISO('P4D'),
                        };
              })
            : [{ start: Duration.fromISO('P0D'), end: Duration.fromISO('P4D') }];
    // get individual dayRange per item and sort according to value
    const timeRangeStart = timeRangeItems.reduce((prev, { start }) => (prev > start ? start : prev), Duration.fromISO('P16D'));
    const timeRangeEnd = timeRangeItems.reduce((prev, { end }) => (prev < end ? end : prev), Duration.fromISO('P-31D'));
    const timeRangeForAllItems = transformDurationsToTimeRange({ start: timeRangeStart, end: timeRangeEnd });
    const startTime = reportTime.startOf(startOfUnit).plus(timeRangeStart);
    const endTime = reportTime.startOf(startOfUnit).plus(timeRangeEnd);

    const timeStepsPerDay = viewblockState.settings.timeStepsPerDay || defaultState.settings.timeStepsPerDay;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const offsetLeft = viewblockState.settings.offsetLeft ? viewblockState.settings.offsetLeft : defaultState.settings.offsetLeft!; //defaultstate will always have fallback values
    const useFixedOffset = viewblockState.settings.useFixedOffset || false;

    const mergedModel = {
        ...model,
        fontSize: viewblockState.settings.fontSize || 100,
        time,
        startTime,
        endTime,
        timeStepsPerDay,
        timeRange: timeRangeForAllItems,
        offsetLeft,
        useFixedOffset,
        title: viewblockState.settings.title || '',
        subTitle: viewblockState.settings.subTitle || '',
        description: viewblockState.settings.description || '',
        enableXAxis: typeof viewblockState.settings.enableXAxis === 'undefined' ? true : !!viewblockState.settings.enableXAxis,
        enableYAxis: typeof viewblockState.settings.enableYAxis === 'undefined' ? true : !!viewblockState.settings.enableYAxis,
        enableNowLine: typeof viewblockState.settings.enableNowLine === 'undefined' ? false : !!viewblockState.settings.enableNowLine,
        enableNowLineText: viewblockState.settings.enableNowLineText || '',
        legendFullWidth: !!viewblockState.settings.legendFullWidth,
        horizontalLines: (viewblockState.settings.horizontalLines || []).map((horizontalLine) => ({
            ...horizontalLine,
            dashArray: getDashArrayFromLineStyle(horizontalLine.lineStyle),
        })),
        canDownload: typeof viewblockState.settings.canDownload === 'undefined' ? false : !!viewblockState.settings.canDownload,
        datasets,
        viewblockState,
    };

    const orderedItems = viewblockState.settings.items.sort((a, b) => a.zOrder - b.zOrder);

    const graphData: IGraphData[] = orderedItems.map((item) => {
        const {
            databilityId,
            directionalDatabilityId,
            style,
            smoothing,
            symbol,
            colour,
            unit,
            unitLabel,
            highlightMinima,
            minimaColour,
            highlightMaxima,
            maximaColour,
            maximaReference,
            minimaReference,
            scope,
            displayExtremes,
            minimaHighlightOnTimeline,
            maximaHighlightOnTimeline,
            dp,
            lineThickness,
            arrowSize,
            arrowLabelFrequency,
        } = item;
        //FIXME: BUG IN HERE. graphData.data.length === 0 after save.
        const datability = viewblockState.databilities.find((datability) => datability.id == databilityId);
        const directionalDatability = viewblockState.databilities.find((datability) => datability.id === directionalDatabilityId);
        const dataset =
            datability && reducedDatasets.find((dataset) => dataset?.datasetId === datability.datasetId && dataset?.capabilityId === datability.capabilityId);

        const directionalCapability = datasets.find(
            (dataset) => dataset.datasetId === directionalDatability?.datasetId && dataset.databilityId === directionalDatabilityId
        )?.capability;

        // let dataForGraph: { x: number; y: string | number | null; timeX: string }[] | { x: number; y: string | number | null; timeX: string }[][] = [];
        const dataForGraph: any[] = [];

        const chosenStyle = chartjsStyles.find((s) => s.type === style) || FALLBACK_CHARTJS_STYLE;
        const pointStyle: string = directionalCapability ? 'none' : symbol || DEFAULT_SYMBOL;
        const smoothingOpts = SMOOTHING_OPTIONS.find((s) => s.type === smoothing) || DEFAULT_CURVE;

        const emptyResponse = {
            ...item,
            color: colour,
            dashArray: getDashArrayFromLineStyle(style),
            pointStyle,
            data: dataForGraph,
            highlightMinima: displayExtremes && highlightMinima,
            minimaColour: minimaColour || colour,
            highlightMaxima: displayExtremes && highlightMaxima,
            maximaColour: maximaColour || colour,
            maximaReference: maximaReference,
            minimaReference: minimaReference,
            min: [],
            max: [],
            maximaHighlightOnTimelineEnabled: displayExtremes && highlightMaxima && maximaHighlightOnTimeline,
            minimaHighlightOnTimelineEnabled: displayExtremes && highlightMinima && minimaHighlightOnTimeline,
            displayUnit: '',
            lineThickness: lineThickness ?? 1,
            arrowSize: arrowSize ?? 1,
            arrowLabelFrequency,
            ...chosenStyle.opts,
            ...smoothingOpts,
        };
        if (!dataset) return emptyResponse;
        const cfjson = dataset.cfjson;
        if (!cfjson) return emptyResponse;

        const wholeCapability = dataset.capability;
        if (!wholeCapability) return emptyResponse;

        const variables = Object.keys(wholeCapability.variables);
        if (variables.some((v) => !isVariableInCfjson(cfjson, v))) {
            return emptyResponse;
        }
        const capUnit = wholeCapability && getUnitMarkupFromCapabilityVariable(wholeCapability);
        const capabilityUnitDefintion = capUnit && getOrMakeUnitDefinitionFromMdsSiMarkup(capUnit);
        const variable = wholeCapability ? Object.keys(wholeCapability.variables)[0] : '';

        const directionalVariable = directionalCapability ? Object.keys(directionalCapability.variables)[0] : '';
        const directionalUnit = directionalCapability && getUnitMarkupFromCapabilityVariable(directionalCapability);

        //Always display directional arrow as 'to'.
        const mdsVariable = directionalCapability?.variables?.[directionalVariable];
        const mdsDirection = mdsVariable?.attributes?.direction;
        const shouldFlip180 = typeof mdsDirection === 'string' && mdsDirection !== 'to';

        const sourceUnit = dataset.cfjson && getUnitDefinitionFromCfJsonForVariable(dataset.cfjson, variable)?.unit;
        const targetUnit = unit || sourceUnit || (capabilityUnitDefintion && capabilityUnitDefintion.unit);
        const latexUnit = (targetUnit && getOrMakeUnitDefinitionFromMdsSiMarkup(targetUnit).niceLatex) || '';
        const displayUnit = !unitLabel || unitLabel == unit ? latexUnit : unitLabel;

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const { start, end } = datability.timeRange
            ? {
                  start: Duration.fromISO(datability.timeRange.split('--')[0]).plus({ days: 1 }),
                  end: Duration.fromISO(datability.timeRange.split('--')[1]).minus({ days: 1 }),
              }
            : {
                  start: Duration.fromISO('P0D'),
                  end: Duration.fromISO('P4D'),
              };

        const interval = Interval.fromDateTimes(reportTime.startOf(startOfUnit).plus(start), reportTime.startOf(startOfUnit).plus(end));

        const data = dataset.data
            .filter((datum: { time: string }) => {
                const datumValue = (() => {
                    if (datum[variable]) return datum[variable];
                    if (dataset.capability && datum[dataset.capability.id]) return datum[dataset.capability.id];
                    if (!dataset.cfjson) return null;

                    const relevantVariable = Object.entries(dataset.cfjson.variables).find(([, v]) => v.attributes.standard_name === variable);
                    if (!relevantVariable) return null;

                    const key = relevantVariable[0];
                    return datum[key];
                })();

                if (datumValue === null || !datum.time) {
                    return false;
                }
                if (!interval.contains(DateTime.fromISO(datum.time))) {
                    return false;
                }
                return true;
            })
            .map((datum: { [x: string]: string | number | null; time: string }) => {
                const { time } = datum;

                const datumTime = DateTime.fromISO(time).setZone(reportTime.zoneName);
                const variable = Object.keys(dataset.capability!.variables)[0];
                const yValue = datum[variable]
                    ? datum[variable]
                    : datum[dataset.capability!.id]
                      ? datum[dataset.capability!.id]
                      : datum[Object.entries(dataset.cfjson!.variables).find(([, v]) => v.attributes.standard_name === variable)![0]];

                const coreData = {
                    x: datumTime.toMillis(),
                    y: yValue,
                    timeX: datumTime.toFormat('HH:mm'),
                    roundedY: yValue ? Number.parseFloat(Number.parseFloat(yValue.toString()).toFixed(dp !== undefined ? dp : DEFAULT_DP)) : null,
                    ...datum,
                };

                const zValue = getZValue({ cfjson: dataset.cfjson, datum, directionalCapability });

                const datumValues = zValue ? { ...coreData, z: shouldFlip180 ? flipValue180(directionalUnit!, zValue) : zValue } : coreData;

                if (!yValue) {
                    return datumValues;
                }
                const convertedYValue = convertValueUnit({ from: sourceUnit!, to: targetUnit!, value: yValue, decimalPrecision: 2 });
                const valueInTargetUnit =
                    unit && unit !== capUnit
                        ? {
                              ...datumValues,
                              y: convertedYValue,
                              roundedY: Number.parseFloat(Number.parseFloat(convertedYValue.toString()).toFixed(dp !== undefined ? dp : DEFAULT_DP)),
                          }
                        : datumValues;

                return valueInTargetUnit;
            });

        const { min, max } = calculateExtremes({ scope, data, timezone: reportTime.zoneName });

        const graphData = {
            ...item,
            color: colour,
            dashArray: getDashArrayFromLineStyle(style),
            pointStyle,
            data: data,
            highlightMinima: displayExtremes && highlightMinima,
            minimaColour: minimaColour || colour,
            highlightMaxima: displayExtremes && highlightMaxima,
            maximaColour: maximaColour || colour,
            maximaReference: maximaReference,
            minimaReference: minimaReference,
            min,
            max,
            maximaHighlightOnTimelineEnabled: displayExtremes && highlightMaxima && maximaHighlightOnTimeline,
            minimaHighlightOnTimelineEnabled: displayExtremes && highlightMinima && minimaHighlightOnTimeline,
            lineThickness: lineThickness ?? 1,
            arrowSize: arrowSize ?? 1,
            arrowLabelFrequency,
            dimensions: Object.keys(dataset?.cfjson?.dimensions ?? {}).filter((d) => d !== 'time'),
            ...chosenStyle.opts,
            ...smoothingOpts,
        };

        return {
            ...graphData,
            displayUnit: viewblockState.settings.yAxisLabel ? '' : displayUnit,
        };
    });

    return { ...state, model: { ...mergedModel, graphData, settings: viewblockState.settings, databilities: viewblockState.databilities } };
};

export default mergeTemplateAndData;

interface getZValueProps {
    directionalCapability?: Capability | null;
    datum: { [x: string]: string | number | null; time: string };
    cfjson: CfJson | null;
}
const getZValue = ({ directionalCapability, datum, cfjson }: getZValueProps) => {
    if (!cfjson) return undefined;
    if (!directionalCapability) return undefined;
    const directionalVariable = directionalCapability ? Object.keys(directionalCapability.variables)[0] : '';

    if (datum[Object.keys(directionalCapability.variables)[0]]) {
        return datum[directionalVariable];
    }

    if (datum[directionalCapability!.id]) {
        return datum[directionalCapability!.id];
    }
    const variableIndex = Object.entries(cfjson.variables).find(([, v]) => v.attributes.standard_name === directionalVariable);
    if (!variableIndex) return undefined;

    return datum[variableIndex[0]];
};

const isVariableInCfjson = (cfjson: CfJson, variable: string) => {
    const standardNamedVariable = Object.values(cfjson.variables).find((v) => {
        if (v.attributes.standard_name === variable) return true;
        return false;
    });
    if (standardNamedVariable) return true;
    if (cfjson.variables[variable]) return true;
    return false;
};
